// @flow

import _ from 'lodash';
// !!! IMPORTANT !!!
//
// This file is shared between the zapier/zapier and zapier/next-js repository.
// Any changes made here should be reflected in there as well.
// For more information, see: https://zapier.quip.com/xaKqAOpdhpY2#ZXFACA8Jnp9
import React, { Component } from 'react';
import bemify, { toModifiers } from 'bemify-js';

import { Icon } from '@zapier/common-components';
import { Link } from '@zapier/design-system';
import { createKeyHandler } from '@zapier/common-utils';
import { type TrackingFunction, useTrackingContext } from '@zapier/tracking';

import MegaMenu from 'web-ssr/app/common/components/ContentHeader/MegaMenu';
import { normalizePathname } from 'web-ssr/app/common/utils/UrlUtils';
import 'web-ssr/app/common/components/ContentHeader/HeaderLinks.scss';

import type { HeaderMediaQueries } from 'web-ssr/app/common/components/ContentHeader/types';
import type { KindType } from 'web-ssr/app/common/types';
import type { MegaMenuLinkCollection } from 'web-ssr/app/common/components/ContentHeader/MegaMenu';

const MEGA_MENU_CLOSE_TIME = 10; // in ms
const MEGA_MENU_OPEN_TIME = 300; // in ms

// Using `v2` here to avoid conflicts with sassy :|
const bem = bemify('navigation-links-v2');

type MegaMenuPageCollection = {
  key: string,
  collections: MegaMenuLinkCollection[],
};

type MegaMenuVisibleState = string | null;

export type HeaderLinkItemProps = {
  title: string,
  href: string,
  isActive?: boolean,
  onToggleMegaMenu?: (key: MegaMenuVisibleState) => void,
  onToggleMegaMenuWithDelay?: (
    key: MegaMenuVisibleState,
    delay: number
  ) => void,
  // Used to set anchor tag `rel` to `external`, this forces `redux-router-kit`
  // to refresh the browser when navigating to this link, e.g. avoid client-side
  // routing. ?boolean,
  kind?: KindType,
  relatedMatches: string[],
  onClick?: (e: SyntheticEvent<any>, trackEvent: TrackingFunction) => void,
  childLinks?: MegaMenuPageCollection,
  visibleMegaMenu?: MegaMenuVisibleState,
  isMobileMenu?: boolean,
  enableMegaMenu?: boolean,
  isVisible: ({
    isLoggedIn: boolean,
    hasTeamAccount: boolean,
    mediaQueries: HeaderMediaQueries,
  }) => boolean,
};

const HeaderLinkItem = (props: HeaderLinkItemProps) => {
  const {
    title,
    href,
    kind,
    isActive,
    enableMegaMenu,
    childLinks = {},
    onClick,
    onToggleMegaMenu,
    onToggleMegaMenuWithDelay,
    visibleMegaMenu,
    isMobileMenu,
  } = props;

  // The `href` acts as an ID here, we use it to both identify the mega menu
  // link container and as the URL that the header text _may_ navigate to ( on
  // mobile the link opens the mega menu, on desktop it is a link).
  const isLinkActive = href === visibleMegaMenu;
  const hasChildLinks = !_.isEmpty(childLinks.collections);

  const { trackEvent } = useTrackingContext();

  const headerNode = (() => {
    // If this is the mobile menu, we need to return a different DOM structure
    // to support the accordion menu. The accordion menu only shows up when
    // the mega menu is enabled.
    if (isMobileMenu && hasChildLinks && enableMegaMenu) {
      return (
        <div
          className={bem('__link', kind, isActive ? '--active' : '')}
          onClick={
            onToggleMegaMenu
              ? () => onToggleMegaMenu(isLinkActive ? null : href)
              : _.noop
          }
          onKeyDown={createKeyHandler(
            ['enter'],
            onToggleMegaMenu
              ? () => onToggleMegaMenu(isLinkActive ? null : href)
              : _.noop
          )}
          role="button"
          tabIndex="0"
        >
          <span className={bem('__inner-link', kind)}>{title}</span>
          <Icon
            className={bem('__arrow-down')}
            name={isLinkActive ? 'arrow-up' : 'arrow-down'}
          />
        </div>
      );
    }

    return (
      <div
        className={bem(
          '__link',
          kind,
          isActive ? '--active' : '',
          isLinkActive ? '--visible-mega' : ''
        )}
      >
        <Link
          color="white"
          data-content={title /* CSS hack used by --selectable */}
          href={href}
          onClick={onClick ? e => onClick(e, trackEvent) : _.noop}
        >
          <span className={bem('__inner-link', kind)}>{title}</span>
        </Link>
      </div>
    );
  })();

  return (
    <div
      className={bem('__item', kind)}
      onMouseEnter={
        !isMobileMenu && onToggleMegaMenuWithDelay
          ? () =>
              onToggleMegaMenuWithDelay(
                href,
                hasChildLinks ? MEGA_MENU_OPEN_TIME : 0
              )
          : _.noop
      }
    >
      {headerNode}
      {enableMegaMenu && (
        <MegaMenu
          kind={isLinkActive ? 'visible' : 'hidden'}
          links={childLinks.collections}
          renderHeaderLink={
            !isMobileMenu
              ? null
              : () => (
                  <Link color="inherit" href={href}>
                    Browse all {title.toLowerCase()}
                  </Link>
                )
          }
        />
      )}
    </div>
  );
};

type HeaderLinksProps = {
  currentUrl: string,
  links: HeaderLinkItemProps[],
  kind?: KindType,
  isMobileMenu?: boolean,
  onClickLink: () => void,
  enableMegaMenu?: boolean,
  ariaLabel?: string,
};

type NavLinksState = {
  visibleMegaMenu: MegaMenuVisibleState,
};

class HeaderLinks extends Component<HeaderLinksProps, NavLinksState> {
  state = {
    visibleMegaMenu: null,
  };

  static defaultProps = {
    enableMegaMenu: true,
  };

  megaMenuTrigger: TimeoutID | null;

  onSetMegaMenuVisbility = (key: MegaMenuVisibleState) => {
    this.setState({
      visibleMegaMenu: key,
    });
  };

  onSetMegaMenuVisbilityWithDelay = (
    key: MegaMenuVisibleState,
    delay: number
  ) => {
    if (this.megaMenuTrigger) {
      clearTimeout(this.megaMenuTrigger);
    }

    if (!this.props.isMobileMenu) {
      this.megaMenuTrigger = setTimeout(() => {
        this.setState({
          visibleMegaMenu: key,
        });

        this.megaMenuTrigger = null;
      }, delay);
    }
  };

  render() {
    const {
      currentUrl,
      links,
      kind,
      onClickLink,
      isMobileMenu,
      enableMegaMenu,
      ariaLabel,
    } = this.props;

    // Passing `true` to `normalizePathname` to ensure that any query / hash
    // string is retained
    const currentUrlNormalized = normalizePathname(currentUrl, true);

    const linkElements = links.map((linkProps, idx) => {
      const isActive: boolean = Boolean(
        currentUrlNormalized === linkProps.href ||
          _.includes(linkProps.relatedMatches, currentUrlNormalized)
      );

      return (
        <HeaderLinkItem
          isMobileMenu={isMobileMenu}
          key={idx}
          {...linkProps}
          enableMegaMenu={enableMegaMenu}
          isActive={isActive}
          onClick={(event, trackEvent) => {
            onClickLink();

            if (linkProps.onClick) {
              linkProps.onClick(event, trackEvent);
            }
          }}
          onToggleMegaMenu={this.onSetMegaMenuVisbility}
          onToggleMegaMenuWithDelay={this.onSetMegaMenuVisbilityWithDelay}
          visibleMegaMenu={this.state.visibleMegaMenu}
        />
      );
    });

    return (
      <nav
        aria-label={ariaLabel}
        className={bem(toModifiers(kind))}
        onMouseLeave={() =>
          this.onSetMegaMenuVisbilityWithDelay(null, MEGA_MENU_CLOSE_TIME)
        }
      >
        {linkElements}
      </nav>
    );
  }
}

export default HeaderLinks;
